import { useAuthStoreShallow } from "@/features/authentication";
import { useGetFoldersQuery } from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";
import {
  UpgradeLabel,
  useLockedAction,
} from "@components/plan-subscription-lock";
import { CursorMenu, type CursorPoint } from "@components/ui/cursor-menu";
import Popover from "@components/ui/popover";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { toast } from "react-toastify";
import {
  IconCursorText,
  IconDots,
  IconFolderPlus,
  IconFolderSymlink,
  IconTrash,
  IconUsersGroup,
} from "@tabler/icons-react";
import { CreateFolderDialog } from "./create-folder-dialog";
import { DeleteFolderDialog } from "./delete-folder-dialog";
import { MembersDialog } from "./members";
import { MoveFolderDialog } from "./move-folder-dialog";
import { RenameFolderDialog } from "./rename-folder-dialog";

const MEMBERS_FEATURE_LOCK_KEY = "stories-folders-members-edit";
const FOLDERS_FEATURE_LOCK_KEY = "stories-folders-limit";

const OPTIONS = [
  {
    label: "New folder",
    value: "new-folder",
    icon: IconFolderPlus,
    locked: true,
  },
  {
    label: "Members",
    value: "members",
    icon: IconUsersGroup,
    locked: true,
  },
  {
    label: "Rename folder",
    value: "rename",
    icon: IconCursorText,
  },
  {
    label: "Move to folder",
    value: "move-to-folder",
    icon: IconFolderSymlink,
  },

  {
    label: "Delete folder",
    value: "delete",
    icon: IconTrash,
  },
];

export const FolderActions = ({
  isActive,
  setIsOpen,
  isOpen,
  folder,
  className,
  placement = "right",
  renderTrigger,
  contextMenuPoint = null,
  onContextMenuClose,
}: {
  isActive: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  folder: IStoriesFolder;
  className?: string;
  placement?: "right" | "bottom";
  /**
   * Replaces the trigger entirely. The sidebar keeps its bare hairline icon; a
   * list row hands in the same button every other action on that row is, which
   * is more than a class string can carry — those buttons are a Button variant,
   * with its own ground, glyph fill and hover.
   *
   * Whatever is returned is cloned with the popover's reference props, so it has
   * to forward a ref and spread the rest.
   */
  renderTrigger?: (state: { isOpen: boolean }) => React.ReactNode;
  /**
   * Where the row was right-clicked, if it was. The same menu the dots button
   * opens, anchored to the pointer instead — a folder row used to hand the
   * right click to the browser while every document row opened its own menu.
   */
  contextMenuPoint?: CursorPoint | null;
  onContextMenuClose?: () => void;
}) => {
  const { data: folders } = useGetFoldersQuery();
  const dispatch = useAppDispatch();
  const isUserOrCreatorLite = useAuthStoreShallow((s) => s.isUserOrCreatorLite);
  const { value: membersLocked, onLockedAction: onMembersLockedAction } =
    useLockedAction({
      feature: MEMBERS_FEATURE_LOCK_KEY,
    });
  const { value: foldersLocked, onLockedAction: onFoldersLockedAction } =
    useLockedAction({
      feature: FOLDERS_FEATURE_LOCK_KEY,
    });

  const hasFoldersLimitation = typeof foldersLocked === "number";
  const isFoldersLocked =
    hasFoldersLimitation &&
    folders?.length &&
    folders?.length >= foldersLocked &&
    foldersLocked !== null;

  const handleOptionSelect = (option: (typeof OPTIONS)[number]) => {
    // Folder management (create/rename/move/delete) is restricted to
    // administrators and creators — matches the server-side creator gate on
    // the /folders write routes. "members" keeps its own plan-lock handling.
    const restrictedActions = [
      "new-folder",
      "rename",
      "move-to-folder",
      "delete",
    ];
    if (isUserOrCreatorLite && restrictedActions.includes(option.value)) {
      toast.info("Only administrators and creators can manage folders.", {
        toastId: "manage-folder-toast",
      });
      return;
    }
    if (option.locked) {
      if (option.value === "members" && membersLocked)
        return onMembersLockedAction();
      if (option.value === "new-folder" && isFoldersLocked)
        return onFoldersLockedAction();
    }
    if (option.value === "new-folder") {
      dispatch(
        openDialog({
          id: CreateFolderDialog.id,
          content: CreateFolderDialog,
          props: {
            folderId: folder._id,
          },
        }),
      );
    }
    if (option.value === "rename") {
      dispatch(
        openDialog({
          id: RenameFolderDialog.id,
          content: RenameFolderDialog,
          props: {
            folder,
          },
        }),
      );
    }
    if (option.value === "members") {
      dispatch(
        openDialog({
          id: MembersDialog.id,
          content: MembersDialog,
          props: {
            folder,
          },
        }),
      );
    }
    if (option.value === "delete") {
      dispatch(
        openDialog({
          id: DeleteFolderDialog.id,
          content: DeleteFolderDialog,
          props: {
            folder,
          },
        }),
      );
    }
    if (option.value === "move-to-folder") {
      dispatch(
        openDialog({
          id: MoveFolderDialog.id,
          content: MoveFolderDialog,
          props: { folderId: folder._id, folderName: folder.name },
        }),
      );
    }
  };

  const menuClassName =
    "tw-w-40 tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5";

  /** The options, closing whichever surface they were picked from. */
  const renderOptions = (close: () => void) =>
    OPTIONS.map((o) => {
      const Icon = o.icon;
      return (
        <button
          type="button"
          key={o.value}
          onClick={(e) => {
            e.stopPropagation();
            close();
            handleOptionSelect(o);
          }}
          className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-100"
        >
          <Icon className="tw-mr-1.5 tw-size-4 tw-text-blue" stroke={1} />
          {o.label}
          {o.locked && o.value === "members" && membersLocked && (
            <UpgradeLabel
              feature={MEMBERS_FEATURE_LOCK_KEY}
              className="tw-ml-auto tw-size-5 tw-rounded-md"
              iconClassName="!tw-text-[11px] tw-text-white"
            />
          )}
          {o.locked && o.value === "new-folder" && isFoldersLocked && (
            <UpgradeLabel
              feature={FOLDERS_FEATURE_LOCK_KEY}
              className="tw-ml-auto tw-size-5 tw-rounded-md"
              iconClassName="!tw-text-[11px] tw-text-white"
            />
          )}
        </button>
      );
    });

  return (
    <>
      <CursorMenu
        point={contextMenuPoint}
        onClose={() => onContextMenuClose?.()}
      >
        <div className={menuClassName}>
          {renderOptions(() => onContextMenuClose?.())}
        </div>
      </CursorMenu>
      <Popover open={isOpen} onOpenChange={setIsOpen} placement={placement}>
        <Popover.Trigger asChild onClick={(e) => e.stopPropagation()}>
          {renderTrigger ? (
            renderTrigger({ isOpen })
          ) : (
            <span
              className={cn(
                "tw-ml-auto tw-flex tw-size-5 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-opacity-0 tw-transition-opacity group-hover/folder:tw-opacity-100",
                (isActive || isOpen) && "tw-opacity-100",
                className,
              )}
            >
              <IconDots className="tw-size-4 tw-text-blue" stroke={1} />
            </span>
          )}
        </Popover.Trigger>
        <Popover.Content className={menuClassName}>
          {renderOptions(() => setIsOpen(false))}
        </Popover.Content>
      </Popover>
    </>
  );
};
